import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { changePasswordV2 } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import * as Yup from 'yup';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SectionEditMode } from '@dealstack/frontend/shared/components/AccountSettings/AccountSettings.types';
import { useStrengthScore } from '@dealstack/frontend/shared/hooks/useStrengthScore.hook.ts';
import { getBackendErrorCode } from '@dealstack/frontend/shared/dataExchange/services/backendCodedError';
import { VERIFICATION_CODE_LENGTH } from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';

export enum CHANGE_PASSWORD_FORM {
	CURRENT_PASSWORD = 'current_password',
	NEW_PASSWORD = 'new_password',
}

export const useManagePasswordController = (
	email: string,
	authenticationFactorId: string | undefined,
	{ isEditModeEnabled, setIsEditModeEnabled }: SectionEditMode
) => {
	const commonYup = useCommonYupValidation();
	const tCommonUI = useTranslationCommonUi();
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [totpCode, setTotpCode] = useState('');
	const [totpCodeError, setTotpCodeError] = useState('');
	const [isTotpCodeDemanded, setIsTotpCodeDemanded] = useState(false);

	const changeUserPasswordRequest = useWrappedRequest(changePasswordV2);

	const validationSchema = Yup.object().shape({
		[CHANGE_PASSWORD_FORM.NEW_PASSWORD]: commonYup.password,
	});

	const passwordChangeForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema),
	});

	const onSubmit = () => {
		const oldPassword = passwordChangeForm.getValues(CHANGE_PASSWORD_FORM.CURRENT_PASSWORD);
		const newPassword = passwordChangeForm.getValues(CHANGE_PASSWORD_FORM.NEW_PASSWORD);

		setTotpCodeError('');

		changeUserPasswordRequest
			.fetch({
				newPassword,
				oldPassword,
				email,
				totpCode: totpCode.length > 0 ? totpCode : undefined,
			})
			.onSuccess(() => {
				setIsEditModeEnabled(false);
				setTotpCode('');
				showToastV2({
					title: tCommonUI('profile.account_settings.password.toast.success.title'),
					message: tCommonUI(
						'profile.account_settings.password.toast.success.description'
					),
				});
			})
			.onAnyError((error: unknown) => {
				const code = getBackendErrorCode(error);
				if (code === ERROR_CODES.AUTH.CREDENTIALS[50013]) {
					setIsTotpCodeDemanded(true);
					setTotpCodeError(tCommonUI('profile.account_settings.totp.missing_code'));
					return;
				}
				if (code === ERROR_CODES.AUTH.CREDENTIALS[50014]) {
					setIsTotpCodeDemanded(true);
					setTotpCodeError(tCommonUI('profile.account_settings.totp.invalid_code'));
					return;
				}
				const errorTitle = tCommonUI('profile.account_settings.password.error');
				passwordChangeForm.setError(CHANGE_PASSWORD_FORM.CURRENT_PASSWORD, {
					type: 'manual',
					message: errorTitle,
				});
			});
	};

	const password = passwordChangeForm.watch(CHANGE_PASSWORD_FORM.NEW_PASSWORD);

	const isPasswordValid =
		!passwordChangeForm.formState.errors[CHANGE_PASSWORD_FORM.NEW_PASSWORD] && !!password;

	const strengthScore = useStrengthScore(isPasswordValid, password);

	const isTotpCodeShown = !!authenticationFactorId || isTotpCodeDemanded;
	const canSubmit =
		isPasswordValid && (!isTotpCodeShown || totpCode.length === VERIFICATION_CODE_LENGTH);

	const onHeaderClick = () => {
		setIsEditModeEnabled(!isEditModeEnabled);
		setTotpCode('');
		setTotpCodeError('');
		setIsTotpCodeDemanded(false);
	};

	return {
		isEditModeEnabled,
		isLoading: changeUserPasswordRequest?.isLoading,
		passwordChangeForm,
		onSubmit,
		showPassword,
		setShowPassword,
		strengthScore,
		isPasswordValid,
		onHeaderClick,
		showCurrentPassword,
		setShowCurrentPassword,
		isTotpCodeShown,
		totpCode,
		setTotpCode,
		totpCodeError,
		canSubmit,
	};
};
